import dayjs from 'dayjs';

import { McpApiKey, OrganizationMcpApiKey } from '@/apis/admin-api';

export type KeyRow = McpApiKey & Partial<Omit<OrganizationMcpApiKey, keyof McpApiKey>>;

export const SDK_DOCS_SCOPE = 'sdk-docs:read';

export const LIBRARY_SCOPES = [
  { scope: 'geometry-library:read', label: 'Geometry library', short: 'Geometry' },
  { scope: 'geometry-component-library:read', label: 'Geometry component library', short: 'Components' },
  { scope: 'material-library:read', label: 'Material library', short: 'Materials' },
  { scope: 'source-directivity-library:read', label: 'Source directivity library', short: 'Directivities' },
];

export const EXPIRY_OPTIONS = [30, 90, 180, 365];
export const DEFAULT_EXPIRY_DAYS = 90;
export const MCP_DOCS_URL = 'https://docs.treble.tech/treble-sdk/getting-started/mcp-server';

export const MCP_BASE_URL: string = import.meta.env.VITE_MCP_URL;
export const MCP_READ_ONLY_URL = `${MCP_BASE_URL}/mcp/read-only`;
export const MCP_DOCS_ONLY_URL = `${MCP_BASE_URL}/mcp/docs`;

export const MCP_SERVER_NAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]{0,62}[a-z0-9])?$/;

export const ALL_LIBRARY_SCOPES = LIBRARY_SCOPES.map(({ scope }) => scope);

export const BUILT_IN_SERVERS = [
  {
    id: 'built-in-read-only',
    name: 'read-only',
    url: MCP_READ_ONLY_URL,
    scopes: [SDK_DOCS_SCOPE, ...ALL_LIBRARY_SCOPES],
  },
  { id: 'built-in-docs', name: 'docs', url: MCP_DOCS_ONLY_URL, scopes: [SDK_DOCS_SCOPE] },
];

// Shows the cell focus outline for keyboard users only.
export const GRID_FOCUS_SX = {
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus-within': { outline: 'none' },
  '&.MuiDataGrid-root .MuiDataGrid-cell:focus-visible, &.MuiDataGrid-root .MuiDataGrid-cell:has(:focus-visible)': {
    outline: '1px solid #00f5ba',
    outlineOffset: '-1px',
  },
};

// Keys created before keys had a server have no server path; they work on any server.
export const getKeyServerUrl = (key: { serverPath: string | null; scopes: string[] }) => {
  if (key.serverPath) return `${MCP_BASE_URL}${key.serverPath}`;
  return key.scopes.some((scope) => scope !== SDK_DOCS_SCOPE) ? MCP_READ_ONLY_URL : MCP_DOCS_ONLY_URL;
};

export const getKeyServerName = (key: { serverPath: string | null; revokedAt: string | null }) => {
  if (key.serverPath) return key.serverPath.slice(key.serverPath.lastIndexOf('/') + 1);
  return key.revokedAt ? '' : 'Any server (older key)';
};

const EXPIRING_SOON_DAYS = 14;

export const getKeyStatus = (key: McpApiKey) => {
  if (key.revokedAt) return 'Revoked';
  if (dayjs(key.expiresAt).isBefore(dayjs())) return 'Expired';
  if (dayjs(key.expiresAt).isBefore(dayjs().add(EXPIRING_SOON_DAYS, 'day'))) return 'Expiring soon';
  return 'Active';
};

export const getOwnerName = (key: KeyRow) =>
  [key.ownerFirstName, key.ownerLastName].filter(Boolean).join(' ') || key.ownerEmail || '';

export const formatDate = (value: string | null | undefined) => (value ? dayjs(value).format('DD/MM/YYYY') : '');

export const fieldSx = {
  'input, label, fieldset, .MuiSelect-select': { fontSize: 14 },
  input: { padding: '13px 14px' },
  label: { marginTop: '-3px' },
};
